import { createContext, MutableRefObject, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import EChartsReactCore from 'echarts-for-react/lib/core';
import { ChartStoreType, createChartStore } from '../store';
import { ChartDataSource, ConsolidatedMode } from '../types';
import { buildChartData, buildEChartsOption, buildLegendItems, computeAxisConfig, ChartData } from '../utils';
import { COLORS } from '../constants';

// ─── Store context ───────────────────────────────────────────────────────────

const ChartStoreContext = createContext<ReturnType<typeof createChartStore> | null>(null);

// ─── ECharts ref context ─────────────────────────────────────────────────────

type EChartsRefContextType = {
    echartsRef: MutableRefObject<EChartsReactCore | null>;
    getEchartsInstance: () => ReturnType<EChartsReactCore['getEchartsInstance']> | undefined;
    chartReady: boolean;
    setChartReady: (ready: boolean) => void;
};

const EChartsRefContext = createContext<EChartsRefContextType | null>(null);

// ─── Chart data context ──────────────────────────────────────────────────────

const ChartDataContext = createContext<ChartData | null>(null);

// ─── Internal data provider ──────────────────────────────────────────────────

function ChartDataProvider({ children }: { children: ReactNode }) {
    const dataSources = useChartStore((s) => s.dataSources);
    const selectedPeriods = useChartStore((s) => s.selectedPeriods);
    const chartCfg = useChartStore((s) => s.chartCfg);
    const showOriginalDataIfLatestIsMissing = useChartStore((s) => s.showOriginalDataIfLatestIsMissing);
    const descending = useChartStore((s) => s.descending);

    const chartData = useMemo(
        () => buildChartData(dataSources, selectedPeriods, chartCfg, showOriginalDataIfLatestIsMissing, descending),
        [dataSources, selectedPeriods, chartCfg, showOriginalDataIfLatestIsMissing, descending],
    );

    return (
        <ChartDataContext.Provider value={chartData}>
            {children}
        </ChartDataContext.Provider>
    );
}

// ─── Main provider ───────────────────────────────────────────────────────────

type ChartProviderProps = {
    dataSources: ChartDataSource[];
    children: ReactNode;
    showOriginalDataIfLatestIsMissing?: boolean;
    descending?: boolean;
    consolidatedMode?: ConsolidatedMode;
    /** When true, all available periods are selected by default instead of only the first one. */
    autoSelectAllPeriods?: boolean;
};

export function ChartProvider({ dataSources, children, showOriginalDataIfLatestIsMissing = false, descending = false, consolidatedMode, autoSelectAllPeriods = false }: ChartProviderProps) {
    const [store] = useState(() => createChartStore());

    // ECharts ref shared by Renderer, Legend, and DownloadModal
    const echartsRef = useRef<EChartsReactCore | null>(null);
    const getEchartsInstance = useCallback(() => echartsRef.current?.getEchartsInstance(), []);
    const [chartReady, setChartReady] = useState(false);

    const echartsRefValue = useMemo<EChartsRefContextType>(() => ({
        echartsRef,
        getEchartsInstance,
        chartReady,
        setChartReady,
    }), [chartReady, getEchartsInstance]);

    // Sync dataSources prop into store (period sync happens atomically inside syncDataSources)
    useEffect(() => {
        store.getState().dataSourceActions.syncDataSources(dataSources, consolidatedMode);
    }, [dataSources, consolidatedMode, store]);

    // Sync showOriginalDataIfLatestIsMissing prop into store
    useEffect(() => {
        store.getState().chartActions.setShowOriginalData(showOriginalDataIfLatestIsMissing);
    }, [showOriginalDataIfLatestIsMissing, store]);

    // Sync descending prop into store
    useEffect(() => {
        store.getState().chartActions.setDescending(descending);
    }, [descending, store]);

    // Sync autoSelectAllPeriods prop into store
    useEffect(() => {
        store.getState().chartActions.setAutoSelectAllPeriods(autoSelectAllPeriods);
    }, [autoSelectAllPeriods, store]);

    return (
        <ChartStoreContext.Provider value={store}>
            <EChartsRefContext.Provider value={echartsRefValue}>
                <ChartDataProvider>
                    {children}
                </ChartDataProvider>
            </EChartsRefContext.Provider>
        </ChartStoreContext.Provider>
    );
}

// ─── Store hook ──────────────────────────────────────────────────────────────

export function useChartStore<T>(selector: (state: ChartStoreType) => T): T {
    const store = useContext(ChartStoreContext);
    if (!store) {
        throw new Error('useChartStore must be used within a ChartProvider');
    }
    return useStore(store, useShallow(selector));
}

// ─── Ref hook ────────────────────────────────────────────────────────────────

export function useEChartsRef(): EChartsRefContextType {
    const ctx = useContext(EChartsRefContext);
    if (!ctx) {
        throw new Error('useEChartsRef must be used within a ChartProvider');
    }
    return ctx;
}

// ─── Chart data hook ─────────────────────────────────────────────────────────

export function useChartData(): ChartData {
    const ctx = useContext(ChartDataContext);
    if (!ctx) {
        throw new Error('useChartData must be used within a ChartProvider');
    }
    return ctx;
}

// ─── Derived data hooks ──────────────────────────────────────────────────────

export function useAxisConfig() {
    const chartData = useChartData();
    return useMemo(
        () => computeAxisConfig(chartData.fieldIdsToChart, chartData.fieldsCfg),
        [chartData.fieldIdsToChart, chartData.fieldsCfg],
    );
}

export function useEChartsOption() {
    const chartData = useChartData();
    const chartCfg = useChartStoreChartCfg();
    const axisConfig = useAxisConfig();
    const hiddenSeries = useChartStoreHiddenSeries();

    return useMemo(
        () => buildEChartsOption({
            periodLabels: chartData.periodLabels,
            fieldIdsToChart: chartData.fieldIdsToChart,
            fieldsCfg: chartData.fieldsCfg,
            seriesData: chartData.seriesData,
            chartCfg,
            axisConfig,
            colors: COLORS,
            hiddenSeries,
            seriesLabelMap: chartData.seriesLabelMap,
        }),
        [chartData, chartCfg, axisConfig, hiddenSeries],
    );
}

export function useLegendItems() {
    const chartData = useChartData();
    return useMemo(
        () => buildLegendItems(chartData.fieldIdsToChart, chartData.fieldsCfg, chartData.fieldsTotalChanges, COLORS, chartData.seriesLabelMap),
        [chartData],
    );
}

// ─── Selector hooks ───────────────────────────────────────────────────────────

export const useChartStoreFieldActions = () => useChartStore((s) => s.fieldActions);
export const useChartStoreChartActions = () => useChartStore((s) => s.chartActions);
export const useChartStoreSeriesActions = () => useChartStore((s) => s.seriesActions);
export const useChartStoreDataSourceActions = () => useChartStore((s) => s.dataSourceActions);

export const useChartStoreDataSources = () => useChartStore((s) => s.dataSources);
export const useChartStoreActiveDataSourceTab = () => useChartStore((s) => s.activeDataSourceTab);
export const useChartStoreAvailablePeriods = () => useChartStore((s) => s.availablePeriods);
export const useChartStoreSelectedPeriods = () => useChartStore((s) => s.selectedPeriods);
export const useChartStoreChartCfg = () => useChartStore((s) => s.chartCfg);
export const useChartStoreHeadingState = () => useChartStore((s) => ({
    heading: s.heading,
    editingHeading: s.editingHeading,
}));
export const useChartStoreHiddenSeries = () => useChartStore((s) => s.hiddenSeries);
export const useChartStoreConsolidatedMode = () => useChartStore((s) => s.consolidatedMode);

export const useChartStoreFieldConfig = (sourceId: number, fieldId: string) =>
    useChartStore((s) => s.dataSources.find((src) => src.id === sourceId)?.fieldsCfg[fieldId]);

// ─── Derived company hooks ──────────────────────────────────────────────────

type CompanyInfo = { companyId: number; companyName: string };

export function useChartCompanies(): CompanyInfo[] {
    const dataSources = useChartStore((s) => s.dataSources);
    return useMemo(() => {
        const seen = new Set<number>();
        const result: CompanyInfo[] = [];
        for (const ds of dataSources) {
            if (!seen.has(ds.companyId)) {
                seen.add(ds.companyId);
                result.push({ companyId: ds.companyId, companyName: ds.companyName });
            }
        }
        return result;
    }, [dataSources]);
}
